import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, LOAN_TYPE_LABEL, inr } from "@/lib/api";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const STAGES = [
  { key: "new", label: "New", color: "#94A3B8" },
  { key: "documents_pending", label: "Docs Pending", color: "#F59E0B" },
  { key: "under_review", label: "Under Review", color: "#0052FF" },
  { key: "approved", label: "Approved", color: "#059669" },
  { key: "disbursed", label: "Disbursed", color: "#10B981" },
  { key: "rejected", label: "Rejected", color: "#DC2626" },
  { key: "closed", label: "Closed", color: "#52525B" },
];

export default function LoanPipeline() {
  const qc = useQueryClient();
  const { data: loans = [] } = useQuery({ queryKey: ["loans-all"], queryFn: async () => (await api.get("/loans")).data });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/loans/${id}`, { status })).data,
    onSuccess: () => { toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["loans-all"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Loan Pipeline</h1>
      <p className="text-sm text-[#52525B]">All loan files across every stage from creation to disbursal.</p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {STAGES.map((s) => {
          const items = loans.filter((l) => l.status === s.key);
          const amount = items.reduce((sum, l) => sum + (l.amount || 0), 0);
          return (
            <div key={s.key} className="lj-kanban-col" data-testid={`kanban-col-${s.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                  <div className="font-display font-bold text-sm text-[#06152D]">{s.label}</div>
                  <span className="text-xs text-[#52525B]">({items.length})</span>
                </div>
                <div className="text-[10px] font-mono-num text-[#52525B]">{amount ? inr(amount).replace("₹", "₹") : "—"}</div>
              </div>
              <div className="space-y-2 max-h-[520px] overflow-y-auto">
                {items.map((l) => (
                  <div key={l.id} className="bg-white border border-[#E4E4E7] rounded-lg p-3" data-testid={`kanban-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <Link to={`/admin/customers/${l.customer_id}`} className="font-mono-num text-[10px] font-semibold text-[#0052FF] hover:underline">{l.file_number || l.application_id}</Link>
                      <Badge variant="secondary" className="text-[10px]">{LOAN_TYPE_LABEL[l.loan_type]}</Badge>
                    </div>
                    <div className="mt-1 text-sm font-semibold text-[#06152D] font-mono-num">{inr(l.amount)}</div>
                    <div className="text-xs text-[#52525B] mt-0.5">Bank: {l.submitted_to_bank || "—"}</div>
                    <div className="mt-2">
                      <Select value={l.status} onValueChange={(v) => updateStatus.mutate({ id: l.id, status: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`kanban-status-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STAGES.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs text-[#94A3B8] py-4 text-center">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
